/**
 * The two scroll axes: everything that differs between vertical and horizontal scrolling.
 *
 * It all lives in one `ScrollAxis` object: the DOM readers used for discovery and geometry, and the
 * two `FadeEdge` objects saying where this axis's fades sit and which way they run. Without it, every
 * reader and every painter in `scroll-affordance.ts` forks on the axis by hand.
 *
 * Neither the geometry decisions nor the rectangle arithmetic are here; both are in
 * `scroll-affordance-geometry.ts`, which this file reads `Bounds` from.
 */

import { type Bounds, type ScrollGeometry } from "./scroll-affordance-geometry";

// -- Tuning (ported from the prototype, .agents/prototypes/better-scrollbars/) ------------------

export const FADE_LENGTH = 36; // px, at any edge with travel remaining

// -- The two axes -------------------------------------------------------------------------------

/**
 * One of the two edges an axis can fade, named exactly as the geometry seam names its decision
 * (`AxisAffordance.fadeAtStart` / `.fadeAtEnd`) and as an `AxisOverlay`'s fade elements are keyed. So
 * one name reaches the decision, the element, and the edge's own drawing rules below.
 */
export type FadeEdgeName = "fadeAtStart" | "fadeAtEnd";

/** Both edge names, so a painter walks an axis's edges instead of forking on start versus end. */
export const FADE_EDGE_NAMES: readonly FadeEdgeName[] = ["fadeAtStart", "fadeAtEnd"];

/**
 * One of the four edges that can fade, and everything drawing it needs. Holding the three values
 * together is what keeps them in step: a class name, the box the fade occupies inside the scroller,
 * and the direction its gradient runs are one edge's rules, not three parallel lists paired by hand.
 */
export type FadeEdge = {
   className: string;
   /** The px box this fade occupies, given the scroller's own box. */
   boundsOf: (box: Bounds) => Bounds;
   /** `linear-gradient` direction: from the surface's own colour at the edge, inward to transparent. */
   gradient: string;
};

/** One axis of an element: everything the readers and painters need in order not to fork on it. */
export type ScrollAxis = {
   /** Key into the geometry seam's per-axis state, and into a `ScrollerEntry`'s overlay elements. */
   name: "vertical" | "horizontal";

   // Reading the scroller.
   /** This axis's computed `overflow-y` / `overflow-x`, for confirming a candidate really scrolls. */
   overflowOf: (style: CSSStyleDeclaration) => string;
   scrollOffsetOf: (element: HTMLElement) => number;
   visibleLengthOf: (element: HTMLElement) => number;
   totalLengthOf: (element: HTMLElement) => number;

   /** The two fades this axis owns, keyed by the same name the geometry seam decides them under. */
   fadeEdges: Record<FadeEdgeName, FadeEdge>;
};

export const verticalAxis: ScrollAxis = {
   name: "vertical",

   overflowOf: (style) => style.overflowY,
   scrollOffsetOf: (element) => element.scrollTop,
   visibleLengthOf: (element) => element.clientHeight,
   totalLengthOf: (element) => element.scrollHeight,

   fadeEdges: {
      fadeAtStart: {
         className: "scroll-affordance-fade-top",
         boundsOf: (box) => ({ left: box.left, top: box.top, right: box.right, bottom: box.top + FADE_LENGTH }),
         gradient: "to bottom",
      },
      fadeAtEnd: {
         className: "scroll-affordance-fade-bottom",
         boundsOf: (box) => ({ left: box.left, top: box.bottom - FADE_LENGTH, right: box.right, bottom: box.bottom }),
         gradient: "to top",
      },
   },
};

export const horizontalAxis: ScrollAxis = {
   name: "horizontal",

   overflowOf: (style) => style.overflowX,
   scrollOffsetOf: (element) => element.scrollLeft,
   visibleLengthOf: (element) => element.clientWidth,
   totalLengthOf: (element) => element.scrollWidth,

   fadeEdges: {
      fadeAtStart: {
         className: "scroll-affordance-fade-left",
         boundsOf: (box) => ({ left: box.left, top: box.top, right: box.left + FADE_LENGTH, bottom: box.bottom }),
         gradient: "to right",
      },
      fadeAtEnd: {
         className: "scroll-affordance-fade-right",
         boundsOf: (box) => ({ left: box.right - FADE_LENGTH, top: box.top, right: box.right, bottom: box.bottom }),
         gradient: "to left",
      },
   },
};

export const SCROLL_AXES: readonly ScrollAxis[] = [verticalAxis, horizontalAxis];

/** The plain numbers the geometry seam needs for one axis of one scroller. */
export function axisGeometry(axis: ScrollAxis, element: HTMLElement): ScrollGeometry {
   return {
      scrollOffset: axis.scrollOffsetOf(element),
      visibleLength: axis.visibleLengthOf(element),
      totalLength: axis.totalLengthOf(element),
   };
}
